import { Star } from "lucide-react";
import { useState } from "react";

import { DEFAULT_SLIDER_CONFIG, SAMPLE_TEXTS } from "./constants";

import { Slider } from "@/components/Slider";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { HumanConfiguration } from "@/types/evaluate";

interface HumanInputPreviewProps {
  configuration: HumanConfiguration | undefined;
}

const getSelectedChoiceClassName = (option: string): string => {
  const normalizedOption = option.trim().toLowerCase();

  if (normalizedOption === "pass") {
    return "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200";
  }

  if (normalizedOption === "fail") {
    return "bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200";
  }

  return "bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200";
};

const MAX_INLINE_RATING_STARS = 5;
const MAX_INLINE_CHOICE_OPTIONS = 3;

const HumanInputPreview = ({ configuration }: HumanInputPreviewProps) => {
  const [sliderValue, setSliderValue] = useState<number>(0);
  const [ratingValue, setRatingValue] = useState<number>(0);
  const [choiceValue, setChoiceValue] = useState<string>("");

  if (!configuration || !configuration.ui_element) {
    return null;
  }

  const { data_type, ui_element } = configuration;

  let previewComponent = null;

  if (ui_element.component === "text") {
    if (data_type === "number") {
      previewComponent = (
        <div className="max-w-[256px]">
          <Input value={SAMPLE_TEXTS.number} type="number" readOnly />
        </div>
      );
    } else {
      previewComponent = (
        <div className="max-w-[256px]">
          <Textarea value={SAMPLE_TEXTS.string} rows={4} readOnly />
        </div>
      );
    }
  } else if (ui_element.component === "slider" && data_type === "number") {
    const min = ui_element.min || DEFAULT_SLIDER_CONFIG.min;
    const max = ui_element.max || DEFAULT_SLIDER_CONFIG.max;
    const value = sliderValue || min;

    previewComponent = (
      <Slider
        value={value.toString()}
        min={min}
        className="max-w-[256px]"
        max={max}
        step={DEFAULT_SLIDER_CONFIG.step}
        hasInput
        onValueChange={val => setSliderValue(Math.round(Number(val)))}
      />
    );
  } else if (ui_element.component === "rating" && data_type === "number") {
    const max = ui_element.max || 5;

    previewComponent =
      max > MAX_INLINE_RATING_STARS ? (
        <select
          value={ratingValue || ""}
          className="h-8 w-full max-w-[220px] rounded-md border border-border/60 bg-background px-2 text-xs"
          onChange={event => setRatingValue(Number(event.target.value))}
        >
          <option value="" disabled>
            Select rating…
          </option>
          {Array.from({ length: max }, (_, index) => {
            const optionValue = index + 1;
            return (
              <option key={optionValue} value={optionValue}>
                {optionValue} / {max}
              </option>
            );
          })}
        </select>
      ) : (
        <div className="flex max-w-[220px] items-center gap-0.5">
          {Array.from({ length: max }, (_, index) => {
            const optionValue = index + 1;
            const isSelected = optionValue <= ratingValue;

            return (
              <button
                key={optionValue}
                type="button"
                className="flex h-7 flex-1 items-center justify-center rounded transition-colors"
                onClick={() => setRatingValue(optionValue)}
              >
                <Star
                  className={cn(
                    "h-4 w-4 transition-colors",
                    isSelected
                      ? "fill-amber-400 text-amber-400"
                      : "fill-transparent text-slate-300",
                  )}
                />
              </button>
            );
          })}
        </div>
      );
  } else if (ui_element.component === "choice" && data_type === "string") {
    const options = ui_element.options.length > 0 ? ui_element.options : ["Pass", "Fail"];
    const selectedValue = choiceValue || options[0];

    previewComponent =
      options.length > MAX_INLINE_CHOICE_OPTIONS ? (
        <select
          value={selectedValue}
          className="h-8 w-full max-w-[320px] rounded-md border border-border/60 bg-background px-2 text-xs"
          onChange={event => setChoiceValue(event.target.value)}
        >
          {options.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : (
        <div className="flex max-w-[320px] items-center overflow-hidden rounded-md bg-slate-100/70 p-0.5">
          {options.map(option => {
            const isSelected = selectedValue === option;
            const selectedClassName = getSelectedChoiceClassName(option);

            return (
              <button
                key={option}
                type="button"
                className={cn(
                  "min-w-0 flex-1 rounded px-2.5 py-1.5 text-xs font-medium transition-colors",
                  isSelected ? selectedClassName : "text-muted-foreground hover:bg-slate-100",
                )}
                onClick={() => setChoiceValue(option)}
              >
                <span className="block truncate">{option}</span>
              </button>
            );
          })}
        </div>
      );
  }

  if (!previewComponent) {
    return null;
  }

  return (
    <div className="rounded-lg border border-border/70 bg-background p-3">
      <div className="text-sm font-medium">Cell preview</div>
      <div className="max-w-md text-xs text-muted-foreground">
        This is how the control will feel inside the table.
      </div>
      <div className="mt-3">{previewComponent}</div>
    </div>
  );
};

export default HumanInputPreview;
